import { useState, useEffect, useCallback } from 'react';
import { getCoupons, createCoupon, updateCoupon, deleteCoupon } from '../../services/adminService';

const CouponManager = () => {
    const [coupons, setCoupons] = useState({});
    const [loading, setLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingCoupon, setEditingCoupon] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        code: '',
        type: 'percentage',
        value: '',
        expiryDate: '2026-12-31',
        description: ''
    });

    const fetchCoupons = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getCoupons();
            setCoupons(data);
        } catch (err) {
            console.error('Fetch coupons error:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCoupons();
    }, [fetchCoupons]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            if (editingCoupon) {
                await updateCoupon(editingCoupon, formData);
            } else {
                await createCoupon(formData);
            }

            await fetchCoupons();
            resetForm();
        } catch (err) {
            alert(err.message || 'Failed to save coupon');
        }
    };

    const handleDelete = async (code) => {
        if (!window.confirm(`Delete coupon ${code}?`)) return;

        try {
            await deleteCoupon(code);
            await fetchCoupons();
        } catch (err) {
            alert(err.message || 'Failed to delete coupon');
        }
    };

    const handleToggleActive = async (code, currentActive) => {
        try {
            await updateCoupon(code, { active: !currentActive });
            await fetchCoupons();
        } catch (err) {
            alert(err.message || 'Failed to update coupon');
        }
    };

    const startEdit = (coupon) => {
        setEditingCoupon(coupon.code);
        setFormData({
            code: coupon.code,
            type: coupon.type,
            value: coupon.value.toString(),
            expiryDate: coupon.expiryDate,
            description: coupon.description
        });
        setShowAddForm(true);
    };

    const resetForm = () => {
        setFormData({
            code: '',
            type: 'percentage',
            value: '',
            expiryDate: '2026-12-31',
            description: ''
        });
        setEditingCoupon(null);
        setShowAddForm(false);
    };

    const couponArray = Object.values(coupons);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-gray-400">
                    <span className="material-symbols-outlined animate-spin text-2xl">progress_activity</span>
                    <span>Loading coupons...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-white text-2xl font-bold">Coupon Management</h2>
                    <p className="text-gray-400 text-sm mt-1">{couponArray.length} total coupons</p>
                </div>
                <button
                    onClick={() => setShowAddForm(!showAddForm)}
                    className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-all"
                >
                    <span className="material-symbols-outlined text-lg">
                        {showAddForm ? 'close' : 'add'}
                    </span>
                    {showAddForm ? 'Cancel' : 'Add Coupon'}
                </button>
            </div>

            {/* Add/Edit Form */}
            {showAddForm && (
                <div className="bg-dark-card border border-border-gray rounded-xl p-6">
                    <h3 className="text-white text-lg font-bold mb-4">
                        {editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}
                    </h3>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Coupon Code */}
                            <div className="flex flex-col gap-2">
                                <label className="text-gray-400 text-sm font-semibold">Coupon Code</label>
                                <input
                                    type="text"
                                    value={formData.code}
                                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                                    className="bg-dark-bg border border-border-gray rounded-lg px-4 py-2 text-white focus:outline-none focus:border-white"
                                    placeholder="SUMMER25"
                                    required
                                    disabled={!!editingCoupon}
                                />
                            </div>

                            {/* Type */}
                            <div className="flex flex-col gap-2">
                                <label className="text-gray-400 text-sm font-semibold">Discount Type</label>
                                <select
                                    value={formData.type}
                                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                    className="bg-dark-bg border border-border-gray rounded-lg px-4 py-2 text-white focus:outline-none focus:border-white"
                                >
                                    <option value="percentage">Percentage (%)</option>
                                    <option value="fixed">Fixed Amount (₹)</option>
                                </select>
                            </div>

                            {/* Value */}
                            <div className="flex flex-col gap-2">
                                <label className="text-gray-400 text-sm font-semibold">
                                    Value {formData.type === 'percentage' ? '(%)' : '(₹)'}
                                </label>
                                <input
                                    type="number"
                                    value={formData.value}
                                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                                    className="bg-dark-bg border border-border-gray rounded-lg px-4 py-2 text-white focus:outline-none focus:border-white"
                                    placeholder={formData.type === 'percentage' ? '25' : '500'}
                                    required
                                    min="0"
                                    max={formData.type === 'percentage' ? '100' : undefined}
                                />
                            </div>

                            {/* Expiry Date */}
                            <div className="flex flex-col gap-2">
                                <label className="text-gray-400 text-sm font-semibold">Expiry Date</label>
                                <input
                                    type="date"
                                    value={formData.expiryDate}
                                    onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                                    className="bg-dark-bg border border-border-gray rounded-lg px-4 py-2 text-white focus:outline-none focus:border-white"
                                    required
                                />
                            </div>
                        </div>

                        {/* Description */}
                        <div className="flex flex-col gap-2">
                            <label className="text-gray-400 text-sm font-semibold">Description</label>
                            <input
                                type="text"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className="bg-dark-bg border border-border-gray rounded-lg px-4 py-2 text-white focus:outline-none focus:border-white"
                                placeholder="Summer discount - 25% off"
                            />
                        </div>

                        {/* Submit */}
                        <div className="flex gap-3">
                            <button
                                type="submit"
                                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold transition-all"
                            >
                                {editingCoupon ? 'Update Coupon' : 'Create Coupon'}
                            </button>
                            {editingCoupon && (
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-semibold transition-all"
                                >
                                    Cancel Edit
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            )}

            {/* Coupons List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {couponArray.length === 0 ? (
                    <div className="col-span-full text-center py-12 text-gray-400">
                        No coupons created yet
                    </div>
                ) : (
                    couponArray.map((coupon) => (
                        <div
                            key={coupon.code}
                            className={`bg-dark-card border rounded-xl p-5 transition-all ${coupon.active ? 'border-green-500/30' : 'border-border-gray opacity-60'
                                }`}
                        >
                            {/* Header */}
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <h3 className="text-white text-lg font-bold">{coupon.code}</h3>
                                    <p className="text-gray-400 text-xs mt-1">
                                        {coupon.type === 'percentage' ? `${coupon.value}% off` : `₹${coupon.value} off`}
                                    </p>
                                </div>
                                <div className={`px-2 py-1 rounded text-xs font-semibold ${coupon.active
                                    ? 'bg-green-500/20 text-green-400'
                                    : 'bg-gray-500/20 text-gray-400'
                                    }`}>
                                    {coupon.active ? 'Active' : 'Inactive'}
                                </div>
                            </div>

                            {/* Description */}
                            <p className="text-gray-300 text-sm mb-3">{coupon.description}</p>

                            {/* Expiry */}
                            <p className="text-gray-500 text-xs mb-4">
                                Expires: {new Date(coupon.expiryDate).toLocaleDateString()}
                            </p>

                            {/* Actions */}
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleToggleActive(coupon.code, coupon.active)}
                                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${coupon.active
                                        ? 'bg-gray-600 hover:bg-gray-700 text-white'
                                        : 'bg-green-500 hover:bg-green-600 text-white'
                                        }`}
                                >
                                    {coupon.active ? 'Deactivate' : 'Activate'}
                                </button>
                                <button
                                    onClick={() => startEdit(coupon)}
                                    className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-semibold transition-all"
                                >
                                    Edit
                                </button>
                                <button
                                    onClick={() => handleDelete(coupon.code)}
                                    className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-semibold transition-all"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default CouponManager;
